import React, { useState } from 'react';
import {
  Container, Paper, TextField, Button, Typography, Box, Alert,
  Grid, Card, IconButton, InputAdornment, LinearProgress, Link as MuiLink
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import HomeIcon from '@mui/icons-material/Home';
import EngineeringIcon from '@mui/icons-material/Engineering';
import HandymanIcon from '@mui/icons-material/Handyman';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';

const roles = [
  { value: 'homeowner', label: 'Homeowner', desc: 'I own a property and want to manage projects.', icon: <HomeIcon sx={{ fontSize: 36 }} /> },
  { value: 'general_contractor', label: 'General Contractor', desc: 'I manage construction projects for clients.', icon: <EngineeringIcon sx={{ fontSize: 36 }} /> },
  { value: 'subcontractor', label: 'Subcontractor', desc: 'I run a trade business and bid on projects.', icon: <HandymanIcon sx={{ fontSize: 36 }} /> },
];

function passwordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '#e0e0e0' };
  let score = 0;
  if (pw.length >= 8) score += 25;
  if (pw.length >= 12) score += 15;
  if (/[A-Z]/.test(pw)) score += 15;
  if (/[a-z]/.test(pw)) score += 10;
  if (/[0-9]/.test(pw)) score += 15;
  if (/[^A-Za-z0-9]/.test(pw)) score += 20;
  score = Math.min(100, score);
  if (score < 40) return { score, label: 'Weak', color: '#c62828' };
  if (score < 70) return { score, label: 'Fair', color: '#ef9f27' };
  if (score < 90) return { score, label: 'Good', color: '#0288d1' };
  return { score, label: 'Strong', color: '#2e7d32' };
}

export default function Register() {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    password: '', confirm: '', role: ''
  });
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const pwStrength = passwordStrength(form.password);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    if (!form.first_name || !form.last_name) return 'First and last name are required';
    if (!form.email) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Invalid email format';
    if (!form.role) return 'Please select your role';
    if (form.password.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Za-z]/.test(form.password) || !/[0-9]/.test(form.password)) return 'Password must include at least one letter and one number';
    if (form.password !== form.confirm) return 'Passwords do not match';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError(''); setSubmitting(true);
    try {
      await api.post('/auth/register', {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        password: form.password,
        role: form.role,
      });
      setSuccess(`Account created. We've sent a verification email to ${form.email}. Check your inbox to activate your account.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a237e', mb: 1 }}>Yaplar</Typography>
          <Box sx={{ fontSize: 64, my: 2 }}>📧</Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Check your email</Typography>
          <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>{success}</Alert>
          <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
            After you verify, your account will be reviewed by our team before you can sign in.
          </Typography>
          <Button variant="contained" component={Link} to="/login" sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 4 }}>
            Go to sign in
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a237e', mb: 0.5 }}>Yaplar</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>Create your account</Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={submit}>
          {/* Role selection */}
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>I am a…</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {roles.map(r => {
              const selected = form.role === r.value;
              return (
                <Grid item xs={12} sm={4} key={r.value}>
                  <Card variant="outlined" onClick={() => setForm({ ...form, role: r.value })}
                    sx={{
                      p: 2, height: '100%', cursor: 'pointer', textAlign: 'center',
                      borderColor: selected ? '#1a237e' : '#e0e0e0',
                      borderWidth: selected ? 2 : 1,
                      bgcolor: selected ? '#f8f9ff' : 'white',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: '#1a237e', boxShadow: 1 },
                    }}>
                    <Box sx={{ color: selected ? '#1a237e' : '#888' }}>{r.icon}</Box>
                    <Typography sx={{ fontWeight: 700, color: selected ? '#1a237e' : '#222', mt: 0.5 }}>{r.label}</Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>{r.desc}</Typography>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Personal info */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField label="First name" value={form.first_name} onChange={set('first_name')} fullWidth required size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Last name" value={form.last_name} onChange={set('last_name')} fullWidth required size="small" />
            </Grid>
          </Grid>

          <TextField label="Email" type="email" value={form.email} onChange={set('email')} fullWidth required size="small" sx={{ mb: 2 }} />
          <TextField label="Phone (optional)" value={form.phone} onChange={set('phone')} fullWidth size="small" sx={{ mb: 2 }} />

          {/* Password */}
          <TextField label="Password" type={showPw ? 'text' : 'password'} value={form.password} onChange={set('password')} fullWidth required size="small" sx={{ mb: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPw(!showPw)}>{showPw ? <VisibilityOff /> : <Visibility />}</IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Minimum 8 characters, including a letter and a number"
          />
          {form.password && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#666' }}>Strength</Typography>
                <Typography variant="caption" sx={{ color: pwStrength.color, fontWeight: 700 }}>{pwStrength.label}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={pwStrength.score} sx={{ height: 6, borderRadius: 3, bgcolor: '#eceff1', '& .MuiLinearProgress-bar': { bgcolor: pwStrength.color } }} />
            </Box>
          )}

          <TextField label="Confirm password" type={showConfirm ? 'text' : 'password'} value={form.confirm} onChange={set('confirm')} fullWidth required size="small" sx={{ mb: 3 }}
            error={form.confirm && form.confirm !== form.password}
            helperText={form.confirm && form.confirm !== form.password ? 'Passwords do not match' : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowConfirm(!showConfirm)}>{showConfirm ? <VisibilityOff /> : <Visibility />}</IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button type="submit" variant="contained" fullWidth size="large" disabled={submitting}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, py: 1.5, mb: 2, fontWeight: 700, '&:hover': { bgcolor: '#283593' } }}>
            {submitting ? 'Creating account…' : 'Create account'}
          </Button>

          <Typography variant="body2" sx={{ textAlign: 'center', color: '#666' }}>
            Already have an account? <MuiLink component={Link} to="/login" sx={{ color: '#1a237e', fontWeight: 600 }}>Sign in</MuiLink>
          </Typography>
        </form>
      </Paper>
    </Container>
  );
}
